import React, { useEffect, useRef, useState } from 'react';
import Utility from '../../../Utility';
import FeaturePaneHeading from './FeaturePaneHeading';
import { ReactComponent as IconCaptions } from './ControlsIcons/cc.svg';
import { ReactComponent as IconSlide } from './RecordingSearchIcons/slide.svg';
import { ReactComponent as IconWritingSurface } from './RecordingSearchIcons/writing-surface.svg';
import './RecordingSearch.css';

const fields = {
	caption: 'caption_text',
	slide: 'slide_ocr_text',
	'writing-surface': 'writing_surface_ocr_text'
};

// Render only search highlight markers as markup; all other content stays text.
export function SearchSnippet({ hit }) {
	const field = fields[hit.document.item_type];
	const highlights = hit.highlights || [];
	const highlight = highlights.find(item => item.field === field) || highlights[0];
	const snippet = (highlight && (highlight.snippet || (highlight.snippets || []).join(' … '))) || hit.document[field] || '';
	return snippet.split(/(<mark>|<\/mark>)/g).reduce((parts, text, index, tokens) => {
		if (text === '<mark>' || text === '</mark>') return parts;
		parts.push(tokens[index - 1] === '<mark>' ? <mark key={index}>{text}</mark> : text);
		return parts;
	}, []);
}

export default function RecordingSearch({ data, trigger, updatePlayerState, active = true }) {
	const hasSlides = !!data.enabledPresets?.slides || (data.enabledPresets?.aux?.length || 0) > 0;
	const writingSurfaces = data.enabledPresets?.whiteboard || [];
	const writingSurfaceName = writingSurfaces.map(source => source.name).filter(Boolean).join(' / ') || 'Writing surface';
	const [keywords, setKeywords] = useState('');
	const [itemType, setItemType] = useState('');
	const [status, setStatus] = useState('idle');
	const [hits, setHits] = useState([]);
	const [found, setFound] = useState(0);
	const [error, setError] = useState('');
	const requestId = useRef(0);
	const inputRef = useRef(null);
	const searchTimer = useRef(null);
	const lastSearch = useRef(null);
	useEffect(() => () => {
		requestId.current++;
		clearTimeout(searchTimer.current);
	}, []);
	useEffect(() => {
		if (active) inputRef.current?.focus();
	}, [active]);

	const reset = () => {
		clearTimeout(searchTimer.current);
		lastSearch.current = null;
		requestId.current++;
		setStatus('idle');
		setHits([]);
		setError('');
	};

	const search = (contentType = itemType, query = keywords) => {
		clearTimeout(searchTimer.current);
		if (!query.trim() || !data.recording.search_token) return;
		const searchKey = JSON.stringify([contentType, query.trim().toLowerCase()]);
		if (lastSearch.current === searchKey) return;
		lastSearch.current = searchKey;
		const id = ++requestId.current;
		setStatus('loading');
		setHits([]);
		setError('');
		const params = new URLSearchParams({
			keywords: query.trim(),
			search_token: data.recording.search_token,
			collection: 'recording_content',
			recording_ids: data.recording.id,
			item_type: contentType
		});
		Utility.callGet('/leccap_api/api/search?' + params.toString(), xhr => {
			if (id !== requestId.current) return;
			try {
				if (xhr.status !== 200) throw new Error('Unable to search. Please try again.');
				const response = JSON.parse(xhr.responseText);
				if (response.error) throw new Error('Search is unavailable. Reload the player and try again.');
				if (response.recording_content_error || !Array.isArray(response.recording_content?.hits)) {
					throw new Error('Unable to search. Please try again.');
				}
				const results = response.recording_content;
				// The token enforces scope on the server; also reject unexpected recordings here.
				setHits(results.hits.filter(hit => String(hit.document.recording_id) === String(data.recording.id))
					.sort((a, b) => a.document.timestamp_ms - b.document.timestamp_ms));
				setFound(results.found);
				setStatus('done');
			} catch (err) {
				lastSearch.current = null;
				setError(err instanceof SyntaxError ? 'Unable to search. Please try again.' : err.message);
				setStatus('error');
			}
		});
	};

	const changeKeywords = event => {
		const query = event.target.value;
		setKeywords(query);
		if (query.trim().toLowerCase() === keywords.trim().toLowerCase()) return;
		reset();
		if (query.trim()) {
			searchTimer.current = setTimeout(() => search(itemType, query), 300);
		}
	};

	return <section className="recording-search" style={{ display: active ? undefined : 'none' }} aria-label="Search recording" onKeyDown={event => { if (event.key !== 'Escape') event.stopPropagation(); }} onKeyUp={event => { if (event.key !== 'Escape') event.stopPropagation(); }}>
		<FeaturePaneHeading title="Search recording" closeLabel="Close search" onClose={() => updatePlayerState({ search: false })} />
		{!data.recording.search_token ? <p className="recording-search-status" role="status">Search is unavailable. Sign in or reload the player and try again.</p> : <>
			<form role="search" aria-label="Search this recording" onSubmit={event => { event.preventDefault(); search(); }}>
				<div className="recording-search-input">
					<input ref={inputRef} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} writingsuggestions="false" type="search" aria-label="Search this recording" placeholder="Search this recording" value={keywords} onChange={changeKeywords} />
				</div>
				<select aria-label="Search content type" value={itemType} onChange={event => { reset(); setItemType(event.target.value); search(event.target.value); }}>
					<option value="">Search all</option>
					<option value="caption">Captions</option>
					{hasSlides && <option value="slide">Slides</option>}
					{writingSurfaces.length > 0 && <option value="writing-surface">{writingSurfaceName}</option>}
				</select>
			</form>
			<div className={'recording-search-status' + (status === 'done' ? ' results' : '')} role="status" aria-live="polite">
				{status === 'idle' && 'Search within this recording.'}
				{status === 'loading' && 'Searching…'}
				{status === 'error' && error}
				{status === 'done' && <>
					Found <strong>{found}</strong> result{found === 1 ? '' : 's'}
					{hits.length > 0 && hits.length < found && ` (showing ${hits.length})`}
					{hits.length === 0 && <p>No results. Try different words or another content type.</p>}
				</>}
			</div>
			<ul className="recording-search-results" aria-label="Search results" aria-busy={status === 'loading'}>
				{hits.map((hit, index) => {
					const seconds = Number(hit.document.timestamp_ms) / 1000;
					return <li key={hit.document.id || index}>
						<button type="button" className="recording-search-result" onClick={() => trigger('seekonly', seconds)}>
							<span className="recording-search-time">{Utility.sec2hms(seconds)}</span>
							<span className="recording-search-text"><SearchSnippet hit={hit} /></span>
							{hit.document.item_type === 'caption' && <IconCaptions viewBox="0.664 3.894 23.672 17.26" className="recording-search-result-icon" aria-hidden="true" />}
							{hit.document.item_type === 'slide' && <IconSlide className="recording-search-result-icon" aria-hidden="true" />}
							{hit.document.item_type === 'writing-surface' && <IconWritingSurface className="recording-search-result-icon" aria-hidden="true" />}
						</button>
					</li>;
				})}
			</ul>
		</>}
	</section>;
}
